import * as React from "react";
import { Box, Icon } from "@amzn/awsui-components-react";
import { FeedbackRating } from "@amzn/ask-legal-domain";

interface StarRatingProps {
    value: FeedbackRating | null;
    onChange?: (rating: FeedbackRating) => void;
    readonly?: boolean;
    size?: "small" | "medium" | "large";
}

export const StarRating: React.FC<StarRatingProps> = ({
    value,
    onChange,
    readonly = false,
    size = "medium"
}) => {
    const [hoverRating, setHoverRating] = React.useState<FeedbackRating | null>(null);

    const getStarSize = () => {
        switch (size) {
            case "small":
                return "16px";
            case "large":
                return "32px";
            default:
                return "24px";
        }
    };

    const handleStarClick = (rating: FeedbackRating) => {
        if (!readonly && onChange) {
            onChange(rating);
        }
    };

    const handleStarHover = (rating: FeedbackRating) => {
        if (!readonly) {
            setHoverRating(rating);
        }
    };

    const handleMouseLeave = () => {
        if (!readonly) {
            setHoverRating(null);
        }
    };

    const getStarIcon = (starPosition: FeedbackRating) => {
        const currentRating = hoverRating || value;
        const isFilled = currentRating && starPosition <= currentRating;

        return (
            <Icon
                name={isFilled ? "star-filled" : "star"}
                size={size === "small" ? "small" : size === "large" ? "large" : "normal"}
                variant={isFilled ? "warning" : "subtle"}
            />
        );
    };

    return (
        <div
            style={{ display: "flex", alignItems: "center" }}
            onMouseLeave={handleMouseLeave}
        >
            {[
                FeedbackRating.ONE_STAR,
                FeedbackRating.TWO_STARS,
                FeedbackRating.THREE_STARS,
                FeedbackRating.FOUR_STARS,
                FeedbackRating.FIVE_STARS
            ].map((rating) => (
                <span
                    key={rating}
                    style={{
                        display: "inline-block",
                        marginRight: "4px",
                        cursor: readonly ? "default" : "pointer",
                        fontSize: getStarSize(),
                        lineHeight: 1
                    }}
                    onClick={() => handleStarClick(rating)}
                    onMouseEnter={() => handleStarHover(rating)}
                >
                    {getStarIcon(rating)}
                </span>
            ))}
        </div>
    );
};
